import { useState, type ReactNode } from 'react';
import type { Facets } from '../lib/api.js';

interface Props {
  facets: Facets | null;
  params: URLSearchParams;
  onChange: (key: string, value: string | string[] | null) => void;
  onClear: () => void;
}

/**
 * Faceted filter panel.
 *
 * Only shows values that exist in the current scope, with counts, so nobody
 * filters their way into an empty grid — the specific frustration this whole
 * system was built to fix.
 */
export function FilterSidebar({ facets, params, onChange, onClear }: Props): ReactNode {
  const [showAllTags, setShowAllTags] = useState(false);

  const selectedTags = params.getAll('tags');
  const hasFilters = [...params.keys()].some((k) => k !== 'sort' && k !== 'order');

  const toggleTag = (name: string): void => {
    const next = selectedTags.includes(name)
      ? selectedTags.filter((t) => t !== name)
      : [...selectedTags, name];
    onChange('tags', next.length > 0 ? next : null);
  };

  const toggleValue = (key: string, value: string): void => {
    onChange(key, params.get(key) === value ? null : value);
  };

  const visibleTags = facets?.tags
    ? showAllTags
      ? facets.tags
      : facets.tags.slice(0, 12)
    : [];

  return (
    <aside className="sidebar">
      <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <strong style={{ fontSize: 13 }}>Filters</strong>
        {hasFilters && (
          <button className="btn sm" onClick={onClear}>
            Clear all
          </button>
        )}
      </div>

      {/* --- type --- */}
      {facets?.kinds && facets.kinds.length > 0 && (
        <div className="facet">
          <h3>Type</h3>
          {facets.kinds.map((kind) => (
            <div
              key={kind.value}
              className={`facet-row${params.get('kind') === kind.value ? ' selected' : ''}`}
              onClick={() => toggleValue('kind', kind.value)}
            >
              <span className="facet-label">{kind.value}</span>
              <span className="count">{kind.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* --- capture date --- */}
      <div className="facet">
        <h3>Captured</h3>
        <div className="stack">
          <div className="range-row">
            <input
              type="date"
              value={params.get('capturedFrom')?.slice(0, 10) ?? ''}
              onChange={(e) => onChange('capturedFrom', e.target.value || null)}
              aria-label="Captured from"
            />
          </div>
          <div className="range-row">
            <input
              type="date"
              value={params.get('capturedTo')?.slice(0, 10) ?? ''}
              onChange={(e) => onChange('capturedTo', e.target.value || null)}
              aria-label="Captured until"
            />
          </div>
        </div>
        {facets?.years && facets.years.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {facets.years.map((year) => (
              <div
                key={year.value}
                className="facet-row"
                onClick={() => {
                  onChange('capturedFrom', `${year.value}-01-01`);
                  onChange('capturedTo', `${year.value}-12-31`);
                }}
              >
                <span className="facet-label">{year.value}</span>
                <span className="count">{year.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- camera --- */}
      {facets?.cameras && facets.cameras.length > 0 && (
        <div className="facet">
          <h3>Camera</h3>
          {facets.cameras.slice(0, 12).map((camera) => (
            <div
              key={camera.camera_model}
              className={`facet-row${
                params.get('cameraModel') === camera.camera_model ? ' selected' : ''
              }`}
              onClick={() => toggleValue('cameraModel', camera.camera_model)}
              title={`${camera.camera_make ?? ''} ${camera.camera_model}`.trim()}
            >
              <span className="facet-label">{camera.camera_model}</span>
              <span className="count">{camera.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* --- lens --- */}
      {facets?.lenses && facets.lenses.length > 0 && (
        <div className="facet">
          <h3>Lens</h3>
          {facets.lenses.slice(0, 8).map((lens) => (
            <div
              key={lens.value}
              className={`facet-row${params.get('lens') === lens.value ? ' selected' : ''}`}
              onClick={() => toggleValue('lens', lens.value)}
            >
              <span className="facet-label">{lens.value}</span>
              <span className="count">{lens.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* --- tags from vision + manual --- */}
      {visibleTags.length > 0 && (
        <div className="facet">
          <h3>Tags</h3>
          {visibleTags.map((tag) => (
            <div
              key={`${tag.kind}:${tag.value}`}
              className={`facet-row${selectedTags.includes(tag.value) ? ' selected' : ''}`}
              onClick={() => toggleTag(tag.value)}
              title={tag.kind === 'manual' ? 'Added by a person' : 'Detected automatically'}
            >
              <span className="facet-label">
                {tag.kind !== 'manual' && '✨ '}
                {tag.value}
              </span>
              <span className="count">{tag.count}</span>
            </div>
          ))}
          {facets && facets.tags.length > 12 && (
            <button className="btn sm" onClick={() => setShowAllTags(!showAllTags)}>
              {showAllTags ? 'Show fewer' : `Show all ${facets.tags.length}`}
            </button>
          )}
        </div>
      )}

      {/* --- people (face clusters) --- */}
      {facets?.people && facets.people.length > 0 && (
        <div className="facet">
          <h3>People</h3>
          {facets.people.slice(0, 10).map((person) => (
            <div
              key={person.value}
              className={`facet-row${
                params.get('faceClusterId') === person.value ? ' selected' : ''
              }`}
              onClick={() => toggleValue('faceClusterId', person.value)}
            >
              <span className="facet-label">{person.label ?? 'Unnamed person'}</span>
              <span className="count">{person.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* --- duration --- */}
      <div className="facet">
        <h3>Duration (seconds)</h3>
        <div className="range-row">
          <input
            type="number"
            min={0}
            placeholder="min"
            value={params.get('durationMin') ?? ''}
            onChange={(e) => onChange('durationMin', e.target.value || null)}
            aria-label="Minimum duration"
          />
          <input
            type="number"
            min={0}
            placeholder="max"
            value={params.get('durationMax') ?? ''}
            onChange={(e) => onChange('durationMax', e.target.value || null)}
            aria-label="Maximum duration"
          />
        </div>
      </div>

      {/* --- resolution --- */}
      <div className="facet">
        <h3>Minimum resolution</h3>
        <select
          value={params.get('widthMin') ?? ''}
          onChange={(e) => onChange('widthMin', e.target.value || null)}
        >
          <option value="">Any</option>
          <option value="1280">720p or better</option>
          <option value="1920">1080p or better</option>
          <option value="2560">1440p or better</option>
          <option value="3840">4K or better</option>
        </select>
      </div>

      {/* --- file format --- */}
      {facets?.extensions && facets.extensions.length > 0 && (
        <div className="facet">
          <h3>Format</h3>
          {facets.extensions.slice(0, 10).map((ext) => (
            <div
              key={ext.value}
              className={`facet-row${params.get('ext') === ext.value ? ' selected' : ''}`}
              onClick={() => toggleValue('ext', ext.value)}
            >
              <span className="facet-label">.{ext.value}</span>
              <span className="count">{ext.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* --- boolean facets --- */}
      <div className="facet">
        <h3>Other</h3>
        <label className="facet-row" style={{ cursor: 'pointer' }}>
          <span className="facet-label">Has location</span>
          <input
            type="checkbox"
            checked={params.get('hasGps') === 'true'}
            onChange={(e) => onChange('hasGps', e.target.checked ? 'true' : null)}
          />
        </label>
        <label className="facet-row" style={{ cursor: 'pointer' }}>
          <span className="facet-label">Has people</span>
          <input
            type="checkbox"
            checked={params.get('hasFaces') === 'true'}
            onChange={(e) => onChange('hasFaces', e.target.checked ? 'true' : null)}
          />
        </label>
        <label className="facet-row" style={{ cursor: 'pointer' }}>
          <span className="facet-label">Has speech</span>
          <input
            type="checkbox"
            checked={params.get('hasTranscript') === 'true'}
            onChange={(e) => onChange('hasTranscript', e.target.checked ? 'true' : null)}
          />
        </label>
        <label className="facet-row" style={{ cursor: 'pointer' }}>
          <span className="facet-label">Include subfolders</span>
          <input
            type="checkbox"
            checked={params.get('recursive') === 'true'}
            onChange={(e) => onChange('recursive', e.target.checked ? 'true' : null)}
          />
        </label>
      </div>
    </aside>
  );
}
